import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import {
	createTheme,
	makeStyles,
	responsiveFontSizes,
	ThemeProvider,
} from '@material-ui/core/styles'
import { PostHogErrorBoundary } from 'posthog-js/react'
import { Suspense, useCallback, useEffect, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { ToastContainer } from 'react-toastify'
import GridNavbar from 'src/components/sections/Navbar/GridNavBar/GridNavbar'
import PublicNavbar from 'src/components/sections/Navbar/PublicNavbar/PublicNavbar'
import ExpiredBanner from 'src/components/ui/Header/ExpiredBanner'
import FreeTrialBanner from 'src/components/ui/Header/FreeTrialBanner'
import AdminWalkthrough from 'src/components/ui/Walkthrough/AdminWalkthrough'
import FreelancerWalkthrough from 'src/components/ui/Walkthrough/FreelancerWalkthrough'
import { useFetchUserInfoQuery } from 'src/redux/api/graphql/userInfoApi'
import { useFetchCustomerDataQuery } from 'src/redux/api/rest/subscriptionApi'
import { setAdminTour } from 'src/redux/slices/settings'
import {
	useAppDispatch,
	useAppSelector,
	useAppThunkDispatch,
} from 'src/utils/hooks/reduxHooks'
import { recordVisitedPath } from 'src/utils/recapGridUtils/recapEntryTracking'
import { handleChunkLoadError } from 'src/utils/versionCheck'
import ChunkLoadErrorImage from '../../assets/images/ChunkLoadErrorImage.png'
import errorImage from '../../assets/images/error.png'
import NavBar from '../sections/Navbar/Navbar'
import '../../main.css'
import ChedarBreadCrumbs from '../ui/BreadCrumbs/ChedarBreadCrumbs'
import Spinner from '../ui/Spinner'
import { Toaster } from '../ui/shadcn/sonner'

let theme = createTheme({
	palette: {
		primary: {
			main: '#364263',
		},
		secondary: {
			main: '#F3B440',
		},
		// @ts-expect-error
		red: {
			main: '#FF0000',
		},
		quotes: {
			main: '#40F3B4',
		},
		projects: {
			main: '#6892C5',
		},
		clients: {
			main: '#FACC15',
		},
		chedarbackground: {
			main: '#F9FAFB',
		},
		black: {
			main: '#000000',
		},
	},
	overrides: {
		MuiButton: {
			root: {
				fontWeight: 400,
			},
		},
		MuiTypography: {
			subtitle2: {
				fontWeight: 400,
			},
		},
	},
})
theme = responsiveFontSizes(theme)

const useStyles = makeStyles((theme) => ({
	root: {
		height: '100vh',
		backgroundColor: '#F9FAFB',
		overflowY: 'scroll',
	},
	breadCrumbContainer: {
		padding: theme.spacing(0.5),
		justifyContent: 'center',
		marginTop: '1rem',
		// marginLeft: '1.6rem',
	},
	mainContent: {
		padding: theme.spacing(1),
	},
	breadCrumbPaper: {
		padding: theme.spacing(0.5),
		color: theme.palette.text.secondary,
		backgroundColor: '#F9FAFB',
		boxShadow: 'none',
	},
	mainContentPaper: {
		padding: theme.spacing(1),
		color: theme.palette.text.secondary,
		flexGrow: 1,
		overflowY: 'auto',
		backgroundColor: '#F9FAFB',
	},
	mainContentDiv: {
		padding: theme.spacing(0.5),
		flexGrow: 1,
		backgroundColor: '#F9FAFB',
	},
	breadCrumb: {
		padding: theme.spacing(0.5),
	},
	rootError: {
		height: '100vh',
		backgroundColor: '#ECECEC',
	},
	flexLayout: {
		fontFamily: 'Open Sans',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	errorImg: {
		height: '60vh',
	},
	errorHeading: {
		fontWeight: 900,
		color: '#454B69',
		textAlign: 'center',
		fontSize: '2rem',
		paddingTop: '1.5rem',
		marginBottom: '1rem',
	},
	errorText: {
		fontWeight: 500,
		color: '#454B69',
		textAlign: 'center',
		fontSize: '1.5rem',
		marginTop: '1rem',
	},
	btn: {
		fontFamily: 'Open Sans',
		fontWeight: 400,
		padding: '20px',
		backgroundColor: '#454B69',
		color: 'white',
		width: '320px',
		margin: '0 auto',
		textAlign: 'center',
		fontSize: '1.5rem',
		borderRadius: '5px',
		cursor: ' ',
		transition: 'all .2s linear',
		'&:hover': {
			backgroundColor: 'rgba(69, 75, 105, 0.9)',
			transition: 'all .2s linear',
		},
	},
	signup: {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		flexDirection: 'column',
		margin: 0,
		padding: 0,
		minHeight: '100vh',
		width: '100vw',
		background: '#1B163A',
		boxSizing: 'border-box',
		fontFamily: `'Roboto', sans-serif`,
		lineHeight: 1,
		letterSpacing: 1,
	},
	overallPaper: {
		backgroundColor: 'white',
		width: '65%',
		margin: '1rem auto',
		minHeight: '90vh',
	},
	'@media (max-width: 1500px)': {
		overallPaper: {
			width: '85%',
		},
	},
	'@media (max-width: 900px)': {
		overallPaper: {
			width: '95%',
		},
	},
	public: {
		minHeight: '100%',
		backgroundColor: '#f9f9f9',
	},
}))

function ErrorFallback() {
	const classes = useStyles()
	const navigate = useNavigate()
	const { t } = useTranslation('Page404')
	const handleNavigation = () => {
		// We navigate to the home page on button click and then reload the page to reset react
		navigate('/')
		window.location.reload()
	}
	const _rootLayoutId = useId()
	return (
		<div id={_rootLayoutId} className={classes.rootError}>
			<div role="alert" className={classes.flexLayout}>
				<img
					src={errorImage}
					alt="Error"
					className={classes.errorImg}
				/>
				<h1 className={classes.errorHeading}>
					{t('errorFallbackTitle')}
				</h1>
				<p className={classes.errorText}>{t('errorFallbackText')} </p>
				<button
					type="button"
					className={classes.btn}
					onClick={() => {
						handleNavigation()
					}}
					onKeyUp={(e) => e.key === 'Enter' && handleNavigation()}
				>
					{t('errorFallbackButton')}
				</button>
			</div>
		</div>
	)
}

function ChunkLoadErrorFallback() {
	const classes = useStyles()
	const { t } = useTranslation('Page404')
	const _rootLayoutId = useId()

	const refreshPage = useCallback(() => {
		handleChunkLoadError()
	}, [])

	// Auto-refresh after a short delay to handle deployment mismatches
	useEffect(() => {
		const timer = setTimeout(() => {
			refreshPage()
		}, 3000) // Wait 3 seconds then auto-refresh

		return () => clearTimeout(timer)
	}, [refreshPage])

	return (
		<div id={_rootLayoutId} className={classes.rootError}>
			<div role="alert" className={classes.flexLayout}>
				<img
					src={ChunkLoadErrorImage}
					alt="Error"
					className={classes.errorImg}
				/>
				<h1 className={classes.errorHeading}>{t('chunkLoadTitle')}</h1>
				<p className={classes.errorText}>{t('chunkLoadText')}</p>
				<p className={classes.errorText}>
					Refreshing page in 3 seconds to load the latest version...
				</p>
				<button
					type="button"
					onClick={refreshPage}
					style={{
						padding: '10px 20px',
						marginTop: '20px',
						backgroundColor: '#1976d2',
						color: 'white',
						border: 'none',
						borderRadius: '4px',
						cursor: 'pointer',
					}}
				>
					Refresh Now
				</button>
			</div>
		</div>
	)
}

export const noBreadCrumbPaths = [
	'/invoices',
	'/proposals',
	'/contacts',
	'/suppliers',
	'/purchases',
	'/purchaseorders',
	'/accountsettings',
	'/teamsettings',
	'/supplierGantt',
	'/dashboard',
	'/banking',
	'/selectbankaccounts',
	'/prepareInvoices',
	'/reviewInvoices',
	'/exportInvoices',
	'/archivedInvoices',
	'/supplierInvoices',
	'/reporting',
	'/payments',
	'/quotelist',
	'/projectlist',
	'/success',
	'/cancel',
	'/billing',
	'/upgradeplan',
]
// Checks if any part of the word has the string /quote in it
export const regexQuote = /^\/quote.*$/
export const regexQuoteList = /^\/quotelist/
export const regexClient = /^\/clientTransaction\/.*$/
export const regexSupplier = /^\/supplierTransaction\/.*$/

export function checkUrlMatch(noCssPaths: string[], url: string) {
	const regex = new RegExp(noCssPaths.join('|'), 'i') // create regex with array of strings, ignoring case
	return regex.test(url) // check if regex matches URL
}
export const noCssPaths = [
	'/signupsuperuser',
	'/signupfreelancer',
	'/confirmcode',
	'/signupchoose',
].map((item) => item.toLowerCase())

export const noSupportPaths = ['/', '/login'].map((item) => item.toLowerCase())

function MainLayout() {
	const classes = useStyles()
	const { pathname } = useLocation()
	// Record the visited path so recap pages can tell whether they were entered
	// from the budget page (→ sync recap) vs another recap view (→ skip). This
	// layout stays mounted across navigations and its effect runs after the
	// freshly-mounted route has already captured the prior path.
	useEffect(() => {
		recordVisitedPath(pathname)
	}, [pathname])
	const dispatch = useAppDispatch()
	const _dispatchThunk = useAppThunkDispatch()
	const userLoginStatus = useAppSelector(
		(state) => state.auth.userLoginStatus
	)
	const userInfoId = useAppSelector((state) => state.auth.userInfo?.id)
	const _rootLayoutId = useId()
	const { data: userInfo } = useFetchUserInfoQuery(
		{
			userInfoId: userInfoId!,
		},
		{
			skip: !userLoginStatus || !userInfoId,
		}
	)
	const userInformation = useAppSelector(
		(state) => state.settings.userInformation
	)
	const isAdminWalkthroughClosed = useAppSelector(
		(state) => state.settings.isAdminWalkthroughClosed
	)
	const isFreelancer = userInfo?.cognitoGroup?.includes('freelancer')
	const hideTour = userInformation?.hideTour

	// This effect initializes the admin tour when navigating to the clients page.
	// It only runs on pathname/hideTour/isFreelancer changes, NOT on stepIndex changes.
	// biome-ignore lint/plugin: clients route should start the tour.
	useEffect(() => {
		if (pathname === '/clients' && !hideTour && !isFreelancer) {
			// Only initialize the tour, don't reset if already running
			dispatch(
				setAdminTour({
					run: true,
					stepIndex: 0,
					isUserMenuOpen: false,
				})
			)
		}
	}, [pathname, hideTour, isFreelancer, dispatch])

	const { data, error } = useFetchCustomerDataQuery(
		// biome-ignore lint/suspicious/noNonNullAssertedOptionalChain: fixed in skip
		userInfo?.currentCompanyTenantId!,
		{
			skip: !userInfo?.currentCompanyTenantId,
		}
	)

	function showTrialBanner() {
		const currentTimeSeconds = Math.trunc(Date.now() / 1000)
		if (
			data?.subscriptionData?.subscriptionStatus === 'trial' &&
			data?.subscriptionData?.subscriptionEndDate &&
			data?.subscriptionData?.subscriptionEndDate > currentTimeSeconds &&
			!data?.subscriptionData?.isEnterpriseUser &&
			userLoginStatus
		) {
			return true
		}
	}

	function showExpiredBanner() {
		return (
			data?.subscriptionData?.subscriptionStatus === 'expired' &&
			userLoginStatus
		)
	}

	if (checkUrlMatch(noCssPaths, pathname.toLowerCase())) {
		return (
			<div className={classes.signup}>
				<Suspense fallback={<Spinner />}>
					<ToastContainer autoClose={2000} />
					<Outlet key={pathname} />
				</Suspense>
			</div>
		)
	}

	if (checkUrlMatch(['/public/quote'], pathname)) {
		return (
			<ThemeProvider theme={theme}>
				<div className={classes.public}>
					<PublicNavbar />
					<Suspense fallback={<Spinner />}>
						<ToastContainer autoClose={2000} />
						<Outlet key={pathname} />
					</Suspense>
				</div>
			</ThemeProvider>
		)
	}

	return (
		<ThemeProvider theme={theme}>
			<div id={_rootLayoutId} className={classes.root}>
				{userInformation && !hideTour && isFreelancer && (
					<FreelancerWalkthrough />
				)}
				{!isAdminWalkthroughClosed && !hideTour && userInformation && (
					<AdminWalkthrough />
				)}
				<Grid container spacing={0}>
					<Grid item xs={12}>
						{showTrialBanner() && <FreeTrialBanner />}
						{showExpiredBanner() && <ExpiredBanner />}
						{/* //* Changes navbar according to which page is being shown */}
						{regexQuote.test(pathname) &&
						!regexQuoteList.test(pathname) ? (
							<GridNavbar />
						) : (
							<NavBar />
						)}
					</Grid>
					{/* //* Shows breadcrumbs if  not in quotesPage and is logged in*/}
					{userLoginStatus &&
						!(
							regexQuote.test(pathname) ||
							noBreadCrumbPaths.some(
								(item) =>
									item.toLowerCase() ===
									pathname.toLowerCase()
							) ||
							regexClient.test(pathname) ||
							regexSupplier.test(pathname)
						) && (
							<Paper className={classes.overallPaper}>
								<Grid
									container
									item
									xs={12}
									spacing={0}
									className={classes.breadCrumbContainer}
								>
									<Grid
										item
										xs={12}
										style={{ padding: '0 22px' }}
									>
										<ChedarBreadCrumbs />
									</Grid>
								</Grid>
								<Grid
									container
									item
									xs={12}
									spacing={1}
									className={classes.mainContent}
								>
									<Grid item xs={12}>
										<Suspense fallback={<Spinner />}>
											<Outlet key={pathname} />
										</Suspense>
									</Grid>
								</Grid>
							</Paper>
						)}
					{(!userLoginStatus ||
						regexQuote.test(pathname) ||
						noBreadCrumbPaths.some(
							(item) =>
								item.toLowerCase() === pathname.toLowerCase()
						) ||
						regexClient.test(pathname) ||
						regexSupplier.test(pathname)) && (
						<Grid
							container
							item
							xs={12}
							spacing={1}
							className={classes.mainContent}
						>
							<Grid item xs={12}>
								<Suspense fallback={<Spinner />}>
									<Outlet key={pathname} />
								</Suspense>
							</Grid>
						</Grid>
					)}
				</Grid>
			</div>
			<ToastContainer autoClose={2000} />
			<Toaster />
			{/* <Chatbot /> */}
			{/* <TrialExpiredModal /> */}
		</ThemeProvider>
	)
}

const myFallback = ({ error }: { error: Error }) => {
	const chunkLoadArray = [
		'ChunkLoadError',
		'chunk load error',
		'Loading chunk',
		'loadingchunk',
	]
	const message = error.message?.toLowerCase()?.trim()
	if (
		message &&
		chunkLoadArray.some((i) => message.includes(i.toLowerCase()))
	) {
		return <ChunkLoadErrorFallback />
	}
	return <ErrorFallback />
}
const MainLayoutErrorBound = () => {
	return (
		<PostHogErrorBoundary
			fallback={(errorData) => {
				const error = errorData.error as Error
				return myFallback({ error })
			}}
		>
			<MainLayout />
		</PostHogErrorBoundary>
	)
}

export default MainLayoutErrorBound
